import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import DateRangeIcon from '@mui/icons-material/DateRange';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { StaticDateRangePicker } from '@mui/x-date-pickers-pro';
import QButton from 'components/QButton';
import { DateTime, Interval } from 'luxon';
import PropTypes from 'prop-types';
import React, { useMemo, useRef, useReducer } from 'react';
import CustomActionbar from './Actionbar';
import CustomLayout from './Layout';
import CustomShortcuts from './Shortcuts';
import ShortcutsSelect from './ShortcutsSelect';
import CustomToolbar from './Toolbar';
import useStyles from './styles';

// Helper functions for range management
const getDisplayedRangeForId = (ranges, selectedRangeId) =>
  ranges?.find((item) => item.id === selectedRangeId)?.label || 'Custom';

const getDatesForId = (map, id) => {
  const item = map.get(id);
  if (item?.getValue) {
    return item.getValue();
  }
  if (item?.value && item?.value?.isLuxonInterval) {
    return [item.value.start, item.value.end];
  }
  return null;
};

const getRangeForId = (id, ranges) => id && ranges && ranges.find((range) => range?.id === id);

const getIdForRange = (ranges, selectedStartDate, selectedEndDate) => {
  const selectedInterval = Interval.fromDateTimes(selectedStartDate, selectedEndDate);

  const found = ranges.find((item) => {
    let shortcutInterval;
    if (item?.getValue) {
      const [start, end] = item.getValue();
      shortcutInterval = Interval.fromDateTimes(start, end);
    } else if (item?.value?.isLuxonInterval) {
      shortcutInterval = item.value;
    }

    return shortcutInterval && shortcutInterval.equals(selectedInterval);
  });

  return found?.id || null;
};

// Helper to create intervals safely
const createInterval = (startDate, endDate) => {
  if (startDate && endDate && startDate.isValid && endDate.isValid) {
    return Interval.fromDateTimes(startDate, endDate);
  }
  return null;
};

// Helper to create onSelect payload
const createSelectPayload = (state, startDate, endDate, selectedRangeId, interval = null) => ({
  id: state.id,
  start: startDate,
  end: endDate,
  interval: interval || createInterval(startDate, endDate),
  selectedRangeId,
  defaultRangeId: state.selectedDefaultId,
});

// Helper to create shortcut selection payload
const createShortcutPayload = (startDate, endDate, selectedShortcutId, displayedRange) => ({
  startDate,
  endDate,
  selectedShortcutId,
  displayedRange,
});

const initState = ({
  startingDate,
  endingDate,
  ranges,
  selectedRangeId,
  defaultRangeId,
  maxDaysBefore,
  maxDaysAfter,
}) => {
  const selectedRange = getRangeForId(selectedRangeId, ranges);
  let rangeStart, rangeEnd;

  if (selectedRange && ranges) {
    const rangesMap = new Map(ranges.map((range) => [range.id, range]));
    const dates = getDatesForId(rangesMap, selectedRangeId);
    [rangeStart, rangeEnd] = dates || [undefined, undefined];
  } else {
    [rangeStart, rangeEnd] = [undefined, undefined];
  }
  const startDate = startingDate || rangeStart || DateTime.local();
  const endDate = endingDate || rangeEnd || DateTime.local().plus({ day: 30 });
  const predefinedRanges = ranges || [];
  const shortCutsMap = ranges ? new Map(ranges.map((i) => [i.id, i])) : new Map();
  const selectedShortcutId = selectedRangeId ?? getIdForRange(predefinedRanges, startDate, endDate) ?? 'CUSTOM';
  const displayedRange = getDisplayedRangeForId(ranges, selectedShortcutId);
  const selectedDefaultId = defaultRangeId || null;
  const rangePosition = 'start'; // 'start' or 'end' depending on which date is to be set
  const minDate = maxDaysBefore >= 0 ? DateTime.local().startOf('day').minus({ day: maxDaysBefore }) : null;
  const maxDate = maxDaysAfter >= 0 ? DateTime.local().endOf('day').plus({ day: maxDaysAfter }) : null;

  return {
    startDate,
    endDate,
    predefinedRanges,
    shortCutsMap,
    selectedShortcutId,
    displayedRange,
    selectedDefaultId,
    rangePosition,
    minDate,
    maxDate,
    errors: { isValidRange: true },
    anchorEl: null,
  };
};

// Validation helpers
const validateDate = (date, minDate, maxDate, dateType) => {
  if (!date || date?.invalid) {
    return `Input error: ${dateType} date is not valid`;
  }
  if (minDate && date < minDate) {
    return `Out of range: select a date on or after ${minDate.toLocaleString()}`;
  }
  if (maxDate && date > maxDate) {
    return `Out of range: select a date on or before ${maxDate.toLocaleString()}`;
  }
  return null;
};

function verifyRange(minDate, maxDate, selectedStart, selectedEnd) {
  const errors = {};

  // Validate individual dates
  errors.startHelperText = validateDate(selectedStart, minDate, maxDate, 'start');
  errors.endHelperText = validateDate(selectedEnd, minDate, maxDate, 'end');

  // Validate date relationship
  if (selectedEnd && selectedStart && selectedEnd < selectedStart) {
    errors.endHelperText = 'Invalid: End date must be on or after Start date';
  }

  // Overall validation
  errors.isValidRange = !(
    errors.startHelperText ||
    errors.endHelperText ||
    selectedStart === null ||
    selectedEnd === null
  );

  return errors;
}

function getSelectValuesForDates(predefinedRanges, startDate, endDate) {
  // Return early if dates are missing
  if (!startDate || !endDate) {
    return { startDate, endDate, selectedShortcutId: null, displayedRange: null };
  }

  // Find matching shortcut or default to custom
  const selectedShortcutId = getIdForRange(predefinedRanges, startDate, endDate) || 'CUSTOM';
  const displayedRange =
    selectedShortcutId === 'CUSTOM' ? 'Custom' : getDisplayedRangeForId(predefinedRanges, selectedShortcutId);

  return { startDate, endDate, selectedShortcutId, displayedRange };
}

function reducer(state, action) {
  switch (action.type) {
    case 'calendarDisplayed':
      return { ...state, anchorEl: action.payload };
    case 'cancel':
      return { ...action.payload };
    case 'shortcutSelected': {
      const errors = verifyRange(state.minDate, state.maxDate, action.payload.startDate, action.payload.endDate);
      return { ...state, ...action.payload, errors: { ...errors } };
    }
    case 'defaultSelected':
      return { ...state, ...action.payload };
    case 'dateRangeChanged': {
      const errors = verifyRange(state.minDate, state.maxDate, action.payload.startDate, action.payload.endDate);
      const updates = getSelectValuesForDates(state.predefinedRanges, action.payload.startDate, action.payload.endDate);
      const newRangePosition =
        action.payload.rangePosition !== undefined ? action.payload.rangePosition : state.rangePosition;
      return { ...state, ...updates, errors: { ...errors }, rangePosition: newRangePosition };
    }
    case 'inputFocus': {
      return { ...state, rangePosition: action.payload };
    }
    default:
      return state;
  }
}

const QDateRangePicker = (props) => {
  const {
    id,
    onSelect,
    allowCustomRange,
    compact,
    disablePastDates,
    disableFutureDates,
    popoverProps,
    renderInput,
    ...rest
  } = props;
  const initialState = useMemo(() => initState(rest), [rest]);
  const [state, dispatch] = useReducer(reducer, initialState);

  // Helper to handle range selection logic
  const selectRange = (selectedRangeId) => {
    if (selectedRangeId === 'CUSTOM') {
      return createShortcutPayload(rest.startingDate, rest.endingDate, 'CUSTOM', 'Custom');
    } else {
      const [startDate, endDate] = getDatesForId(state.shortCutsMap, selectedRangeId) || [undefined, undefined];
      const displayedRange = getDisplayedRangeForId(state.predefinedRanges, selectedRangeId);
      return createShortcutPayload(startDate, endDate, selectedRangeId, displayedRange);
    }
  };

  // Sync state with selectedRangeId prop changes
  React.useEffect(() => {
    if (rest.selectedRangeId != null && rest.selectedRangeId !== '') {
      const payload = selectRange(rest.selectedRangeId);
      dispatch({ type: 'shortcutSelected', payload });
    }
    // Only run when selectedRangeId changes, or when custom dates change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rest.selectedRangeId, rest.startingDate, rest.endingDate]);

  // Component setup
  const buttonRef = useRef();
  const { classes, cx } = useStyles();
  const keyboardButtonProps = useMemo(() => ({ classes: { root: classes.clearDateIcon } }), [classes.clearDateIcon]);

  // Event handlers
  const handleDateSelection = ([startDate, endDate]) => {
    // Determine range position based on what changed and current position
    let { rangePosition } = state;

    // If we're in start position and now have a start date, move to end
    if (rangePosition === 'start' && startDate && !endDate) {
      rangePosition = 'end';
    }
    // If we're in end position and both dates are set, stay in end position
    // This allows users to continue modifying the end date

    dispatch({ type: 'dateRangeChanged', payload: { startDate, endDate, rangePosition } });
  };

  const handleShortcutSelect = (selectedShortcutId) => {
    if (!selectedShortcutId) return;

    if (selectedShortcutId === 'CUSTOM') {
      // Handle custom selection - keep current dates but update selection
      const payload = createShortcutPayload(state.startDate, state.endDate, 'CUSTOM', 'Custom');
      dispatch({ type: 'shortcutSelected', payload });
    } else {
      // Handle predefined range selection
      const payload = selectRange(selectedShortcutId);
      dispatch({ type: 'shortcutSelected', payload });

      if (!allowCustomRange) {
        // Immediately notify parent for non-custom range selections
        const selectPayload = createSelectPayload(state, payload.startDate, payload.endDate, selectedShortcutId);
        onSelect(selectPayload);
      }
    }
  };

  const handleAccept = () => {
    dispatch({ type: 'calendarDisplayed', payload: null });
    const selectPayload = createSelectPayload(state, state.startDate, state.endDate, state.selectedShortcutId);
    onSelect(selectPayload);
  };

  const handleClose = () => {
    dispatch({ type: 'cancel', payload: initialState });

    // Only call onSelect if the current state is different from initial state
    // This prevents unnecessary updates when user cancels without changes
    const hasChanges =
      state.startDate !== initialState.startDate ||
      state.endDate !== initialState.endDate ||
      state.selectedShortcutId !== initialState.selectedShortcutId;

    if (hasChanges) {
      const selectPayload = createSelectPayload(
        initialState,
        initialState.startDate,
        initialState.endDate,
        initialState.selectedShortcutId,
      );
      onSelect(selectPayload);
    }
  };

  const handleDefaultSelect = (selectedDefaultId) =>
    selectedDefaultId && dispatch({ type: 'defaultSelected', payload: { selectedDefaultId } });

  const handleInputFocus = (event) => {
    event.target.select();
    // Set range position based on which input was focused
    const inputId = event.target.id;
    const rangePosition = inputId === 'start' ? 'start' : 'end';
    dispatch({ type: 'inputFocus', payload: rangePosition });
  };

  const handleInputChange = (input, value) => {
    const startDate = input === 'start' ? value : state.startDate;
    const endDate = input === 'end' ? value : state.endDate;
    const rangePosition = input === 'start' ? 'end' : 'start';

    dispatch({ type: 'dateRangeChanged', payload: { startDate, endDate, rangePosition } });
  };

  const handleInputClear = (input) => {
    const startDate = input === 'start' ? null : state.startDate;
    const endDate = input === 'end' ? null : state.endDate;
    const rangePosition = !startDate && !endDate ? 'start' : input;
    dispatch({ type: 'dateRangeChanged', payload: { startDate, endDate, rangePosition } });
  };

  // Display helpers
  const dateRangeString = () => {
    if (!state.startDate || !state.endDate) {
      return 'Select dates';
    }

    // Normalize dates to DateTime objects if needed
    const startDate = state.startDate?.toLocaleString ? state.startDate : DateTime.fromISO(state.startDate);
    const endDate = state.endDate?.toLocaleString ? state.endDate : DateTime.fromISO(state.endDate);

    if (!startDate.isValid || !endDate.isValid) {
      return 'Invalid dates';
    }

    return `${startDate.toLocaleString(DateTime.DATE_MED)} - ${endDate.toLocaleString(DateTime.DATE_MED)}`;
  };

  const shouldDisableDate = (date) =>
    (state.minDate && date < state.minDate) || (state.maxDate && date > state.maxDate);

  const showCalendar = (e) => dispatch({ type: 'calendarDisplayed', payload: e?.target });

  const renderDefaultInput = (onClickHandler) => (
    <QButton
      onClick={onClickHandler}
      ref={buttonRef}
      className={cx(classes.buttonSelect, compact && 'compact')}
      startIcon={<DateRangeIcon />}
      endIcon={
        <ArrowDropDownRoundedIcon
          sx={{
            color: 'icon.main',
            transform: state.anchorEl ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
          }}
        />
      }
    >
      {compact && state.displayedRange === 'Custom' ? dateRangeString() : state.displayedRange}
    </QButton>
  );

  // Main component render
  return (
    <Box className={classes.dateRangeContainer}>
      {allowCustomRange ? (
        <>
          {renderInput ? renderInput(showCalendar) : renderDefaultInput(showCalendar)}

          <Popover
            id={id || 'date-range-picker-popover'}
            open={Boolean(state.anchorEl)}
            anchorEl={state.anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            {...popoverProps}
            onClose={handleClose}
          >
            <div className={classes.root}>
              <StaticDateRangePicker
                shouldDisableDate={shouldDisableDate}
                value={[state.startDate, state.endDate]}
                onChange={handleDateSelection}
                disablePast={disablePastDates}
                disableFuture={disableFutureDates}
                rangePosition={state.rangePosition}
                slots={{
                  shortcuts: state.shortCutsMap.size ? CustomShortcuts : null,
                  layout: CustomLayout,
                  actionBar: CustomActionbar,
                  toolbar: CustomToolbar,
                }}
                slotProps={{
                  shortcuts: {
                    shortcutItems: state.predefinedRanges,
                    selectedShortcutId: state.selectedShortcutId,
                    handleShortcutSelect,
                  },
                  actionBar: {
                    shortcutItems: state.predefinedRanges,
                    selectedDefaultId: state.selectedDefaultId,
                    handleAccept,
                    handleClose,
                    handleDefaultSelect,
                    isValidRange: state.errors.isValidRange,
                  },
                  toolbar: {
                    startDate: state.startDate,
                    endDate: state.endDate,
                    rangePosition: state.rangePosition,
                    handleInputChange,
                    handleInputFocus,
                    handleInputClear,
                    keyboardButtonProps,
                    errors: state.errors,
                  },
                }}
                calendars={2}
              />
            </div>
          </Popover>
        </>
      ) : (
        <ShortcutsSelect
          shortcutItems={state.predefinedRanges}
          selectedId={state.selectedShortcutId}
          handleSelect={handleShortcutSelect}
        />
      )}

      {!compact && (
        <Typography variant='bodyRegular' className={classes.dateRangeType}>
          {dateRangeString()}
        </Typography>
      )}
    </Box>
  );
};

QDateRangePicker.propTypes = {
  id: PropTypes.string,
  startingDate: PropTypes.object,
  endingDate: PropTypes.object,
  popoverProps: PropTypes.object,
  ranges: PropTypes.array,
  selectedRangeId: PropTypes.string,
  defaultRangeId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  disablePastDates: PropTypes.bool,
  disableFutureDates: PropTypes.bool,
  maxDaysBefore: PropTypes.number,
  maxDaysAfter: PropTypes.number,
  allowCustomRange: PropTypes.bool, // if false, show ranges as a dropdown.  if true, show calendars to allow selection
  compact: PropTypes.bool, // use a compact input rendering
  renderInput: PropTypes.func,
};

export default QDateRangePicker;
